let fixedSavedFrame:CannedFrame|undefined;
import {mountCannedSavedReport, type CannedFrame} from "./cannedReport.ts";
import {originDto} from "./cannedReportModel.ts";
import {cannedReportRequest, cannedReportFile} from "./api.ts";
import {hideHoverHelp} from "./hoverHelp.ts";
import {reportHomeFrame, reportSavedList} from "./reportHome.ts";
import {BASIS_LABELS, compositePresentation, firstDataset} from "./compositeReportModel.ts";
import {reportArrival} from "./reportArrival.ts";
import {type ReportCreationMethod} from "./reportCreationMethod.ts";
import {mountReportSchedules} from "./reportSchedules.ts";
import {mountReportSharing, type ReportAudienceDto} from "./reportSharing.ts";
import {mountRecipientTemplates} from "./reportTemplates.ts";
import {mountPersonalTiles, mountPinControl, type PersonalTilesHost} from "./reportTiles.ts";
import {type ReportWizardStep} from "./reportWizard.ts";
// CRB1 (docs/build-briefs/CRB1-custom-report-builder-core.md; design/mockups/report-builder/03a and 03c, Snip & Sip Light,
// 1440x900): the manual custom report builder, drawn inside the Reports group's own frame (ui/src/reports.ts owns the
// title, the nav and the Export button). It builds a version 1 spec from pickers, runs it through the server, and draws the
// table with a totals row and the business date, time zone, locations and as-of time of the run.
//
// Admin surface: a keyboard and pointer are assumed, but every fixed vocabulary is a picker (data, locations, dates,
// grouping, measures, tests, rows). Free text is only a title, a text value and a number. Text is set with textContent
// only. Every gate resolves a permission: Run needs report.custom.build, the Export control needs it and report.export
// (reports.ts), and a person who may only open the builder sees it locked. The server re-checks everything.

import {archiveSavedReport, copySavedReport, getCustomReportCatalog, getSavedReport, getSavedReportVersion, listSavedReportAudienceChoices, listSavedReports, reportTileRequest, runSavedReport, setSavedReportAudience, type CustomReportCatalogDto, type CustomReportResultDto, type SavedReportDetailDto, type SavedReportListDto, type SavedReportTabId} from "./api.ts";
import {CR_EXPORT_ROW_CAP, NOT_AVAILABLE, compareCells, compareHeads, compareSpanText, dateSpanText, formatCell, freshBuilder, isFigure, locationsText, mayRun, rowCountText, stateFromSpec, totalsRow, truncationText, valueLabelsOf, type BuilderState} from "./customReportModel.ts";
import {zoneFriendlyName} from "./discountSpecialEditor.ts";
import {CHART_WIDTH_BUILDER, CHART_WIDTH_SAVED, renderChartCard} from "./reportChart.ts";
import {buildChartModel, chartOf, type ChartSpecDto} from "./reportChartModel.ts";
import {SAVED_TABS, editedCell, hasMoreMenu, locationsCell, mayArchiveSaved, mayCopySaved, mayEditSaved, mayShareSaved, mayStartReport, savedMeta, scheduleCell, tabCount, versionChoices, visibilityTag} from "./savedReportsModel.ts";
import {hasPermission, type Session} from "./session.ts";

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) element.setAttribute(key, value);
  for (const child of children) element.append(child);
  return element;
}

/** What reports.ts needs to know after a run, and the one thing the screen needs from it. */
export interface CustomReportHooks {
  /** A run finished (true) or the result was cleared (false): the Export control follows. */
  onRan: (hasRun: boolean) => void;
  /** RPTLAYOUT1-F1-S3: the guided builder opens in the Reports frame. New report and Edit as new version both go there. */
  openBuilder: (editing?: { spec: unknown; id: string; baseVersion: number }) => void;
  /** RPTMINE1: show only this template, opened and run (the Reports home chose it); Back returns to the home. */
  templateId?: string;
  back?: () => void;
  /** A saved copy of the template opens in the Reports frame's results. */
  openResults?: (id: string) => void;
}

interface View {
  /** RPTMINE1: a template the Reports home asked to open; the template library opens and runs it once, then forgets it. */
  openTemplateId: string | undefined;
  templatesOnly: boolean;
  creationMethod: ReportCreationMethod | null;
  wizardStep: ReportWizardStep;
  completionMessage: string;
  builderSaved: {stamp: string; id: string; version: number} | undefined;
  session: Session | undefined;
  catalog: CustomReportCatalogDto | undefined;
  state: BuilderState | undefined;
  search: string;
  result: CustomReportResultDto | undefined;
  /** The spec the shown result came from, which Export sends. */
  ranSpec: Record<string, unknown> | undefined;
  running: boolean;
  /** A sentence about the last attempt that did not run. */
  error: string;
  /** Bumped by every open, reset and run, so a late answer discards itself. */
  generation: number;
  ranMs: number | undefined;
  /** CRB2: the Reports home (the saved list), one saved report, or the builder. */
  mode: "home" | "saved";
  list: SavedReportListDto | undefined;
  tab: SavedReportTabId;
  query: string;
  listError: string;
  saved: SavedReportDetailDto | undefined;
  savedResult: CustomReportResultDto | undefined;
  savedError: string;
  savedLoading: boolean;
  /** The older version on screen; undefined is the current one. */
  shownVersion: number | undefined;
  menuOpen: boolean;
  confirmArchive: boolean;
  /** The report being edited as a new version, and the version it was opened at. */
  editing: { id: string; baseVersion: number } | undefined;
  builderAudience: ReportAudienceDto;
  builderAudienceDirty: boolean;
  builderCanShare:boolean;
  /** Makes a retried first save return the report already made. */
  requestId: string;
  saving: boolean;
}

let view: View = freshView();
let hooks: CustomReportHooks | undefined;
let host: HTMLElement | undefined;

function freshView(): View {
  return {
    openTemplateId: undefined,
    templatesOnly: false,
    creationMethod: null,
    wizardStep: "sources",
    completionMessage: "",
    builderSaved: undefined,
    session: undefined,
    catalog: undefined,
    state: undefined,
    search: "",
    result: undefined,
    ranSpec: undefined,
    running: false,
    error: "",
    generation: 0,
    ranMs: undefined,
    mode: "home",
    list: undefined,
    tab: "all",
    query: "",
    listError: "",
    saved: undefined,
    savedResult: undefined,
    savedError: "",
    savedLoading: false,
    shownVersion: undefined,
    menuOpen: false,
    confirmArchive: false,
    editing: undefined,
    builderAudience: {mode:"private",revision:1,recipientIds:[]},
    builderAudienceDirty:false,
    builderCanShare:true,
    requestId: newRequestId(),
    saving: false,
  };
}

function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** What the Export control sends: an unsaved report's spec, or a saved report by id (and the older version on screen). */
export type CustomExportTarget =
  | { kind: "spec"; spec: unknown }
  | { kind: "saved"; id: string; version: number | undefined };

/** The report on screen, for the Export control. Undefined until a run has finished. */
export function customReportExportTarget(): CustomExportTarget | undefined {
  if (view.mode === "saved") {
    return view.saved !== undefined && view.savedResult !== undefined
      ? { kind: "saved", id: view.saved.id, version: view.shownVersion }
      : undefined;
  }
  return undefined;
}

function notifyExport(): void {
  hooks?.onRan(customReportExportTarget() !== undefined);
}

/** Wipes the builder and the result (sign-out, a different session). */
export function resetCustomReport(): void {
  fixedSavedFrame?.destroy();fixedSavedFrame=undefined;
  hideHoverHelp();
  const generation = view.generation + 1;
  view = freshView();
  view.generation = generation;
  host?.replaceChildren();
}

function capitalize(message: string): string {
  return message.length === 0 ? message : `${message[0]?.toUpperCase() ?? ""}${message.slice(1)}`;
}

function mayBuild(): boolean {
  return view.session !== undefined && mayRun((permission) => hasPermission(view.session as Session, permission));
}

/** Opens the builder in `into`. A different session than last time resets first; the same session keeps its choices. */
export async function openCustomReport(
  into: HTMLElement,
  session: Session,
  nextHooks: CustomReportHooks,
): Promise<void> {
  host = into;
  hooks = nextHooks;
  if (view.session !== undefined && view.session.userId !== session.userId) resetCustomReport();
  view.session = session;
  view.generation += 1;
  const generation = view.generation;
  if (view.catalog === undefined) {
    into.replaceChildren(h("p", { cls: "cr-loading", text: "Loading.", testid: "cr-loading" }));
    const result = await getCustomReportCatalog(session.apiBaseUrl, session.tenantId);
    if (generation !== view.generation) return;
    if (result.status !== 200 || !Array.isArray(result.body.catalog?.datasets)) {
      into.replaceChildren(
        h("p", {
          cls: "cr-error",
          text: capitalize(result.body.message ?? "Could not open the report builder."),
          testid: "cr-error",
        }),
      );
      return;
    }
    view.catalog = result.body;
  }
  const first = view.catalog.catalog.datasets[0];
  if (view.state === undefined && first !== undefined) view.state = freshBuilder(first.id);
  // CRB2: the entry is the Reports home, the saved list. "New report" is what opens the builder.
  view.mode = "home";
  view.menuOpen = false;
  view.confirmArchive = false;
  view.error = "";
  notifyExport();
  view.templatesOnly = nextHooks.templateId !== undefined;
  view.openTemplateId = nextHooks.templateId;
  const arrival = view.templatesOnly ? undefined : reportArrival("tenant", session.tenantId, true);
  if (arrival !== undefined) {await openSaved(arrival.definitionId, arrival.version);return;}
  await loadList(generation);
}

// ---------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------

function render(): void {
  hideHoverHelp();
  if (host === undefined || view.catalog === undefined) return;
  if (view.mode === "home") {
    renderHome();
    return;
  }
  renderSaved();
}

function personalTilesHost(mode: "home" | "saved", savedId?: string, version?: number): PersonalTilesHost | null {
  const captured = view.session; const generation = view.generation;
  if (captured === undefined) return null;
  return {
    api: <T>(method: "GET" | "POST", suffix: string, body?: unknown) => reportTileRequest<T & {message?:string}>(captured.apiBaseUrl, captured.tenantId, method, suffix, body),
    active: () => view.session === captured && view.generation === generation && view.mode === mode
      && (savedId === undefined || (view.saved?.id === savedId && (view.shownVersion ?? view.saved.currentVersion) === version)),
    canManage: held("report.custom.build"),
    openSaved: (id, selectedVersion) => {void openSaved(id, selectedVersion);},
  };
}

/** CRB3: the chart card of a result, drawn from the result itself. In the builder the chart is the one Show as names (so a
 * change shows at once, no second query); on a saved report it is the chart the saved spec carries. */
function chartCardFor(result: CustomReportResultDto, asked?: ChartSpecDto): HTMLElement | undefined {
  const slot = asked ?? chartOf(result.spec);
  if (slot === undefined) return undefined;
  const dataset = view.catalog?.catalog.datasets.find((d) => d.id === result.meta.dataset);
  return renderChartCard(buildChartModel(compositePresentation(result), slot, valueLabelsOf(dataset)), view.mode === "saved" ? CHART_WIDTH_SAVED : CHART_WIDTH_BUILDER);
}

function asOfText(result: CustomReportResultDto): string {
  const zone = result.meta.timezones[0];
  try {
    return new Date(result.meta.generatedAt).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      ...(zone === undefined ? {} : { timeZone: zone }),
    });
  } catch {
    return new Date(result.meta.generatedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
}

function compareCell(cell: { text: string; tone: string; part: string }): HTMLElement {
  return h("td", {
    cls: `num cr-cmp-${cell.part}${cell.part === "change" ? ` cr-change-${cell.tone}` : ""}${cell.text === NOT_AVAILABLE ? " na" : ""}`,
    text: cell.text,
  });
}

function resultCard(result: CustomReportResultDto): HTMLElement {
  const card = h("div", { cls: "cr-card", testid: "cr-result" });
  const permitted = view.catalog?.locations.length ?? result.meta.locations.length;
  // A snapshot dataset (Stock on hand) has no dates.
  const dated = view.catalog?.catalog.datasets.find((d) => d.id === result.meta.dataset)?.dated !== false;
  const head = h("div", { cls: "cr-card-head" }, [
    h("b", { text: rowCountText(result), testid: "cr-row-count" }),
    ...(view.ranMs === undefined
      ? []
      : [h("span", { cls: "cr-ran", text: `Ran ${(view.ranMs / 1000).toFixed(1)} s`, testid: "cr-ran" })]),
  ]);
  card.append(head);
  card.append(
    h("div", { cls: "cr-meta", testid: "cr-meta" }, [
      h("span", { text: dated ? dateSpanText(result.meta.from, result.meta.to) : "As of now", testid: "cr-meta-dates" }),
      h("span", { text: result.meta.timezones.map(zoneFriendlyName).join(", "), testid: "cr-meta-zone" }),
      h("span", { text: locationsText(result.meta.locations, permitted), testid: "cr-meta-locations" }),
      h("span", { text: `${result.meta.datasetLabel}, as of ${asOfText(result)}`, testid: "cr-meta-asof" }),
      ...(result.compare === undefined ? [] : [h("span", { text: compareSpanText(result), testid: "cr-meta-compare" })]),
    ]),
  );

  if (result.meta.sources !== undefined) card.append(h("div", {cls:"cr-meta"}, result.meta.sources.map(source => h("span", {text:`${view.catalog?.catalog.datasets.find(d=>d.id===source.dataset)?.label??"Unknown source"}: ${BASIS_LABELS[source.dateBasis]??"-"}`}))));
  const dataset = view.catalog?.catalog.datasets.find((d) => d.id === result.meta.dataset);
  const labels = valueLabelsOf(dataset);

  const table = h("table", { cls: "cr-table", testid: "cr-table" });
  const headRow = h("tr");
  for (const column of result.columns) {
    headRow.append(
      h("th", { cls: isFigure(column) ? "num" : "", text: column.label, attrs: { scope: "col" }, testid: `cr-th-${column.key}` }),
    );
  }
  for (const head of compareHeads(result)) {
    headRow.append(h("th", { cls: "num", text: head.label, attrs: { scope: "col" }, testid: `cr-th-${head.key}` }));
  }
  table.append(h("thead", {}, [headRow]));
  const body = h("tbody");
  result.rows.forEach((row, rowIndex) => {
    const tr = h("tr", { testid: "cr-row" });
    row.forEach((cell, index) => {
      const column = result.columns[index];
      if (column === undefined) return;
      tr.append(
        h("td", {
          cls: `${isFigure(column) ? "num" : ""}${cell === null ? " na" : ""}`.trim(),
          text: formatCell(result.meta.sources !== undefined && column.kind === "dimension" ? (result.dimensionLabels?.[rowIndex]?.[index] ?? (["location_id","product_id","employee_id","drink_id"].includes(column.key) ? null : cell)) : cell, column.type, labels[column.field]),
        }),
      );
    });
    for (const cell of compareCells(result, rowIndex)) tr.append(compareCell(cell));
    body.append(tr);
  });
  table.append(body);
  const totals = totalsRow(result);
  if (totals !== null) {
    const foot = h("tr", { cls: "cr-total", testid: "cr-totals" });
    totals.forEach((cell, index) => {
      const column = result.columns[index];
      foot.append(h("td", { cls: column !== undefined && isFigure(column) ? "num" : "", text: cell }));
    });
    for (const cell of compareCells(result, "totals")) foot.append(compareCell(cell));
    table.append(h("tfoot", {}, [foot]));
  }
  card.append(h("div", { cls: "cr-table-wrap" }, [table]));
  const cut = truncationText(result, CR_EXPORT_ROW_CAP);
  if (cut !== "") card.append(h("p", { cls: "cr-cut", text: cut, testid: "cr-truncated" }));
  return card;
}

// ---------------------------------------------------------------------
// Running
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// CRB2: the Reports home, a saved report, and saving
// ---------------------------------------------------------------------

function held(permission: string): boolean {
  return view.session !== undefined && hasPermission(view.session, permission);
}

function serverMessage(result: { status?:number; networkError?: boolean; body: { message?: string | undefined } }, fallback: string): string {
  if(result.status===401||result.status===403){view.result=undefined;view.ranSpec=undefined;view.savedResult=undefined;notifyExport();}
  return capitalize(
    result.networkError === true ? "Could not reach the server. Check your connection." : (result.body.message ?? fallback),
  );
}

async function loadList(generation: number): Promise<void> {
  const session = view.session;
  if (session === undefined) return;
  if (view.list === undefined) {
    host?.replaceChildren(h("p", { cls: "cr-loading", text: "Loading.", testid: "cr-loading" }));
  }
  const result = await listSavedReports(session.apiBaseUrl, session.tenantId, view.tab, view.query);
  if (generation !== view.generation || view.mode !== "home") return;
  if (result.status !== 200 || !Array.isArray(result.body.items)) {
    view.listError = serverMessage(result, "Could not open your reports.");
  } else {
    view.listError = "";
    view.list = result.body;
  }
  render();
}

function renderHome(): void {
  if(host===undefined||view.catalog===undefined)return;
  let create:HTMLElement|undefined;
  if(mayStartReport(held,view.catalog.catalog.datasets.length)){
    create=h("button",{cls:"cr-btn-primary",text:"New report",testid:"cr-new",attrs:{type:"button"}});
    create.addEventListener("click",startNew);
  }
  const tiles=h("div");const tileHost=personalTilesHost("home");if(tileHost!==null)mountPersonalTiles(tiles,tileHost);
  const afterList=h("div");
  const session = view.session;
  if (session !== undefined) {
    const generation = view.generation;
    mountRecipientTemplates(afterList, {
      baseUrl: session.apiBaseUrl, tenantId: session.tenantId, canCopy: held("report.custom.build"),
      isCurrent: () => view.session === session && view.generation === generation && view.mode === "home",
      field: (id,datasetId)=>view.catalog?.catalog.datasets.find(dataset=>dataset.id===datasetId)?.fields.find(field=>field.name===id),
      dateBasis: id=>view.catalog?.catalog.datasets.find(dataset=>dataset.id===id)?.composite?.dateBasis,
      datasetLabel: id=>view.catalog?.catalog.datasets.find(dataset=>dataset.id===id)?.label??"Unknown source",
      fieldLabel: (id, datasetId) => view.catalog?.catalog.datasets.find((dataset) => dataset.id === datasetId)?.fields.find((field) => field.name === id)?.label ?? id,
      renderResult: resultCard, openCopy: (id) => { if (hooks?.openResults !== undefined) hooks.openResults(id); else void openSaved(id); },
      ...(view.openTemplateId === undefined ? {} : { autoOpen: view.openTemplateId }),
    });
    view.openTemplateId = undefined;
  }
  if (view.templatesOnly) {
    const back = h("button", { cls: "cr-btn", text: "‹ Reports", testid: "cr-templates-back", attrs: { type: "button" } });
    back.addEventListener("click", () => hooks?.back?.());
    host.replaceChildren(back, afterList);
    return;
  }
  host.replaceChildren(reportHomeFrame({create,notice:view.completionMessage,error:view.listError,tiles:tileHost===null?undefined:tiles,list:listCard(),afterList}));
}

function listCard(): HTMLElement {
  const tabs = h("div", { cls: "cr-tabs", attrs: { role: "tablist" } });
  for (const tab of SAVED_TABS) {
    const on = tab.id === view.tab;
    const button = h(
      "button",
      {
        cls: `cr-tab${on ? " on" : ""}`,
        testid: `cr-tab-${tab.id}`,
        attrs: { type: "button", role: "tab", "aria-selected": on ? "true" : "false" },
      },
      [tab.label, h("span", { cls: "cr-tab-count", text: tabCount(tab.id, view.list?.counts), testid: `cr-tab-count-${tab.id}` })],
    );
    button.addEventListener("click", () => {
      if (view.tab === tab.id) return;
      view.tab = tab.id;
      view.generation += 1;
      void loadList(view.generation);
    });
    tabs.append(button);
  }
  const search = h("input", {
    cls: "cr-search cr-list-search",
    testid: "cr-list-search",
    attrs: { type: "search", placeholder: "Search", "aria-label": "Search reports", value: view.query },
  }) as HTMLInputElement;
  search.value = view.query;
  search.addEventListener("input", () => {
    view.query = search.value;
    view.generation += 1;
    const generation = view.generation;
    const session = view.session;
    if (session === undefined) return;
    void listSavedReports(session.apiBaseUrl, session.tenantId, view.tab, view.query).then((result) => {
      if (generation !== view.generation || view.mode !== "home" || result.status !== 200 || !Array.isArray(result.body.items)) return;
      view.list = result.body;
      const body = document.querySelector<HTMLElement>("[data-testid='cr-list-body']");
      if (body !== null) body.replaceWith(listBody());
      for (const tab of SAVED_TABS) {
        const count = document.querySelector<HTMLElement>(`[data-testid='cr-tab-count-${tab.id}']`);
        if (count !== null) count.textContent = tabCount(tab.id, view.list?.counts);
      }
    });
  });
  return reportSavedList({tabs,search,headers:[],body:listBody()});
}

function listBody(): HTMLElement {
  const items = view.list?.items ?? [];
  const permitted = view.list?.permittedLocationCount ?? 0;
  const body = h("div", { cls: "cr-list-body", testid: "cr-list-body" });
  if (items.length === 0) {
    body.append(h("div", { cls: "cr-list-empty", text: "No saved reports yet. Build a report and save it to see it here.", testid: "cr-list-empty" }));
    return body;
  }
  const rows = h("tbody");
  for (const item of items) {
    const open = h("button", { cls: "cr-name", text: item.title, testid: `cr-open-${item.id}`, attrs: { type: "button" } });
    open.addEventListener("click", () => void openSaved(item.id));
    const tag = visibilityTag(item.visibility);
    const name = h("td", { cls: "cr-name-cell" }, [open, ...(tag === "" ? [] : [h("span", { cls: "cr-tag", text: tag, testid: "cr-tag" })])]);
    rows.append(
      h("tr", { testid: "cr-list-row" }, [
        name,
        h("td", { text: item.datasetLabel }),
        h("td", { text: locationsCell(item, permitted) }),
        h("td", { text: scheduleCell(item), cls: "na" }),
        h("td", { text: editedCell(item.editedAt), cls: "num" }),
      ]),
    );
  }
  return reportSavedList({headers:["Name","Data","Locations","Schedule",{label:"Edited",className:"num"}],body:rows}).body;
}

function startNew(): void {
  if (!mayBuild()) return;
  hooks?.openBuilder();
}

async function openSaved(id: string, noteVersion?: number): Promise<void> {
  fixedSavedFrame?.destroy();fixedSavedFrame=undefined;
  const session = view.session;
  if (session === undefined) return;
  view.mode = "saved";
  view.saved = undefined;
  view.savedResult = undefined;
  view.savedError = "";
  view.savedLoading = true;
  view.shownVersion = noteVersion;
  view.menuOpen = false;
  view.confirmArchive = false;
  view.generation += 1;
  const generation = view.generation;
  notifyExport();
  render();
  const got = await getSavedReport(session.apiBaseUrl, session.tenantId, id);
  if (generation !== view.generation) return;
  if (got.status !== 200 || typeof got.body.id !== "string") {
    view.savedLoading = false;
    view.savedError = serverMessage(got, "This report could not be loaded.");
    render();
    return;
  }
  view.saved = got.body;
  const exactVersion=noteVersion??got.body.currentVersion;
  const selected=exactVersion===got.body.currentVersion?got:await getSavedReportVersion(session.apiBaseUrl,session.tenantId,id,exactVersion);
  if(generation!==view.generation)return;
  if(selected.status!==200){view.savedLoading=false;view.savedError=serverMessage(selected,"This report could not be loaded.");render();return;}
  const origin=selected.body.cannedOrigin;
  if(origin!==undefined&&origin!==null){
    if(!originDto(origin)||host===undefined){view.savedLoading=false;view.savedError=NOT_AVAILABLE;render();return;}
    view.savedLoading=false;notifyExport();const prefix=`/tenants/${encodeURIComponent(session.tenantId)}/reports`;
    fixedSavedFrame=mountCannedSavedReport(host,{prefix:`${prefix}/canned`,customPrefix:`${prefix}/custom`,active:()=>view.session?.token===session.token&&view.generation===generation&&view.mode==="saved",
      request:<T>(method:"GET"|"POST",path:string,body?:unknown)=>cannedReportRequest<T>(session.apiBaseUrl,path,method,body),
      file:(path,body)=>cannedReportFile(session.apiBaseUrl,path,body),
      setAudience:(id,audience)=>setSavedReportAudience(session.apiBaseUrl,session.tenantId,id,audience),
    },{definitionId:id,version:exactVersion,origin,title:selected.body.title,versions:got.body.versions,
      openVersion:version=>void openSaved(id,version),back:()=>{fixedSavedFrame?.destroy();fixedSavedFrame=undefined;view.mode="home";view.generation++;void loadList(view.generation);}});
    return;
  }
  await runShown(generation);
}

/** Runs the saved report on screen, at the version shown, as the signed-in person: the server re-checks everything. */
async function runShown(generation: number): Promise<void> {
  const session = view.session;
  const saved = view.saved;
  if (session === undefined || saved === undefined) return;
  view.savedLoading = true;
  view.savedError = "";
  view.savedResult = undefined;
  notifyExport();
  render();
  const started = performance.now();
  const result = await runSavedReport(session.apiBaseUrl, session.tenantId, saved.id, view.shownVersion);
  if (generation !== view.generation) return;
  view.savedLoading = false;
  if (result.status !== 200 || !Array.isArray(result.body.rows)) {
    view.result = undefined; view.ranSpec = undefined; notifyExport();
    view.savedError = serverMessage(result, "That report did not run.");
    render();
    return;
  }
  view.savedResult = result.body;
  view.ranMs = performance.now() - started;
  notifyExport();
  render();
}

function menuItem(testid: string, label: string, onClick: () => void): HTMLElement {
  const item = h("button", { cls: "cr-menu-item", text: label, testid, attrs: { type: "button", role: "menuitem" } });
  item.addEventListener("click", onClick);
  return item;
}

function renderSaved(): void {
  if (host === undefined || view.catalog === undefined) return;
  const root = h("div", { cls: "cr cr-saved", testid: "cr-saved" });
  const back = h("button", { cls: "cr-back", text: "Reports", testid: "cr-back", attrs: { type: "button" } });
  back.addEventListener("click", () => {
    view.mode = "home";
    view.generation += 1;
    notifyExport();
    void loadList(view.generation);
  });
  root.append(back);
  const saved = view.saved;
  if (saved === undefined) {
    root.append(
      view.savedError !== ""
        ? h("p", { cls: "cr-error", text: view.savedError, testid: "cr-error", attrs: { role: "alert" } })
        : h("p", { cls: "cr-loading", text: "Loading.", testid: "cr-loading" }),
    );
    host.replaceChildren(root);
    return;
  }
  const ds = firstDataset(saved.spec, view.catalog.catalog.datasets);
  const editable = ds !== undefined && stateFromSpec(saved.spec, ds, view.catalog?.catalog.datasets) !== undefined;
  const permitted = view.catalog.locations.length;
  const shownCurrent = view.shownVersion === undefined || view.shownVersion === saved.currentVersion;

  const actions = h("div", { cls: "cr-actions" });
  const tileVersion = view.shownVersion ?? saved.currentVersion;
  const tileHost = personalTilesHost("saved", saved.id, tileVersion);
  if (tileHost !== null) mountPinControl(actions, tileHost, {
    definitionId:saved.id, version:tileVersion,
    spec:shownCurrent ? saved.spec : view.savedResult?.spec,
  });
  if (tileHost !== null) mountReportSchedules(root, tileHost, {definitionId:saved.id,version:tileVersion});
  if (mayEditSaved(held, saved) && editable) {
    const edit = h("button", { cls: "cr-btn-quiet cr-btn-outline", text: "Edit", testid: "cr-edit", attrs: { type: "button" } });
    edit.addEventListener("click", () => void editSaved());
    actions.append(edit);
  }
  if (hasMoreMenu(held, saved)) {
    const more = h("button", {
      cls: "cr-btn-quiet cr-btn-outline cr-more",
      text: "...",
      testid: "cr-more",
      attrs: { type: "button", "aria-label": "More", "aria-haspopup": "menu", "aria-expanded": view.menuOpen ? "true" : "false" },
    });
    more.addEventListener("click", () => {
      view.menuOpen = !view.menuOpen;
      view.confirmArchive = false;
      render();
    });
    actions.append(more);
    if (view.menuOpen) {
      const menu = h("div", { cls: "cr-menu", testid: "cr-menu", attrs: { role: "menu" } });
      if (mayCopySaved(held)) menu.append(menuItem("cr-copy", "Save a copy", () => void copyNow()));
      if (mayShareSaved(held, saved)) menu.append(menuItem("cr-share", "Sharing", () => void shareNow()));
      if (mayArchiveSaved(held, saved)) {
        menu.append(
          menuItem("cr-archive", "Archive", () => {
            view.menuOpen = false;
            view.confirmArchive = true;
            render();
          }),
        );
      }
      actions.append(menu);
    }
  }
  const head = h("div", { cls: "cr-bar cr-saved-head" }, [
    h("h2", { cls: "cr-saved-title", text: saved.title, testid: "cr-saved-title" }),
    actions,
  ]);
  root.append(head);
  root.append(
    h(
      "div",
      { cls: "cr-meta cr-saved-meta", testid: "cr-saved-meta" },
      savedMeta(saved, permitted).map((part) => h("span", { text: part })),
    ),
  );
  if (view.confirmArchive) {
    const yes = h("button", { cls: "cr-btn-primary", text: "Archive", testid: "cr-archive-confirm", attrs: { type: "button" } });
    yes.addEventListener("click", () => void archiveNow());
    const no = h("button", { cls: "cr-btn-quiet", text: "Cancel", testid: "cr-archive-cancel", attrs: { type: "button" } });
    no.addEventListener("click", () => {
      view.confirmArchive = false;
      render();
    });
    root.append(h("div", { cls: "cr-confirm", testid: "cr-archive-bar" }, [h("span", { text: `Archive ${saved.title}?` }), yes, no]));
  }
  if (saved.versions.length > 1) {
    const picker = h("select", { cls: "cr-select cr-version", testid: "cr-version", attrs: { "aria-label": "Version" } }) as HTMLSelectElement;
    for (const choice of versionChoices(saved)) {
      const option = document.createElement("option");
      option.value = choice.value;
      option.textContent = choice.label;
      picker.append(option);
    }
    picker.value = String(view.shownVersion ?? saved.currentVersion);
    picker.addEventListener("change", () => {
      const chosen = Number(picker.value);
      view.shownVersion = chosen === saved.currentVersion ? undefined : chosen;
      view.generation += 1;
      void runShown(view.generation);
    });
    root.append(h("div", { cls: "cr-version-row" }, [picker]));
  }
  if (!shownCurrent) {
    // An older version is read-only here; Edit opens it in the builder and Save writes a new version after the current one.
    root.append(h("div", { cls: "cr-meta", testid: "cr-old-version" }, [h("span", { text: `Version ${view.shownVersion}` })]));
  }
  if (view.savedError !== "") {
    root.append(h("p", { cls: "cr-error", text: view.savedError, testid: "cr-error", attrs: { role: "alert" } }));
  } else if (view.savedLoading) {
    root.append(h("p", { cls: "cr-loading", text: "Loading.", testid: "cr-loading" }));
  } else if (view.savedResult !== undefined) {
    const chart = chartCardFor(view.savedResult);
    if (chart !== undefined) root.append(chart);
    root.append(resultCard(view.savedResult));
  }
  host.replaceChildren(root);
}

/** Opens the saved report in the builder as a new version of it. An older version on screen is what opens. */
async function editSaved(): Promise<void> {
  const session = view.session;
  const saved = view.saved;
  if (session === undefined || saved === undefined || !mayBuild()) return;
  const generation=view.generation;
  const token=session.token;
  const savedId=saved.id;
  const selectedVersion=view.shownVersion??saved.currentVersion;
  const current=()=>view.session===session&&view.session.token===token&&view.generation===generation
    &&view.mode==='saved'&&view.saved?.id===savedId&&view.saved.currentVersion===saved.currentVersion
    &&(view.shownVersion??view.saved.currentVersion)===selectedVersion&&mayBuild();
  const ds = firstDataset(saved.spec, view.catalog?.catalog.datasets ?? []);
  if (ds === undefined) return;
  let spec: unknown = saved.spec;
  if (view.shownVersion !== undefined && view.shownVersion !== saved.currentVersion) {
    const older = await getSavedReportVersion(session.apiBaseUrl, session.tenantId, savedId, selectedVersion);
    if(!current())return;
    if (older.status !== 200 || older.body.spec === undefined) {
      view.savedError = serverMessage(older, NOT_AVAILABLE);
      render();
      return;
    }
    spec = older.body.spec;
  }
  if(!current())return;
  const state = stateFromSpec(spec, ds, view.catalog?.catalog.datasets);
  if (state === undefined) {
    view.savedError = NOT_AVAILABLE;
    render();
    return;
  }
  hooks?.openBuilder({ spec, id: saved.id, baseVersion: saved.currentVersion });
}

async function copyNow(): Promise<void> {
  const session = view.session;
  const saved = view.saved;
  if (session === undefined || saved === undefined || !mayCopySaved(held)) return;
  view.menuOpen = false;
  const result = await copySavedReport(session.apiBaseUrl, session.tenantId, saved.id, newRequestId());
  if (result.status !== 201 || typeof result.body.id !== "string") {
    view.savedError = serverMessage(result, "That report did not copy.");
    render();
    return;
  }
  await openSaved(result.body.id);
}

async function shareNow(): Promise<void> {
  const session=view.session;const saved=view.saved;
  if(session===undefined||saved===undefined||saved.audience===undefined||!mayShareSaved(held,saved)||host===undefined)return;
  view.menuOpen=false;render();
  const generation=view.generation;
  const current=()=>view.session===session&&view.generation===generation&&view.mode==="saved"&&view.saved?.id===saved.id;
  mountReportSharing(host,{
    audience:saved.audience,isCurrent:current,
    choices:async()=>{const got=await listSavedReportAudienceChoices(session.apiBaseUrl,session.tenantId);return got.status===200?{items:got.body.items}:{error:serverMessage(got,"People could not load.")};},
    save:async audience=>{
      const got=await setSavedReportAudience(session.apiBaseUrl,session.tenantId,saved.id,audience);
      if(!current())return "";
      if(got.status!==200||typeof got.body.id!=="string")return serverMessage(got,"That change did not save.");
      view.saved=got.body;return undefined;
    },
    done:()=>{if(current())render();},
  });
}

async function archiveNow(): Promise<void> {
  const session = view.session;
  const saved = view.saved;
  if (session === undefined || saved === undefined || !mayArchiveSaved(held, saved)) return;
  view.confirmArchive = false;
  const result = await archiveSavedReport(session.apiBaseUrl, session.tenantId, saved.id);
  if (result.status !== 200) {
    view.savedError = serverMessage(result, "That report did not archive.");
    render();
    return;
  }
  view.mode = "home";
  view.saved = undefined;
  view.savedResult = undefined;
  view.generation += 1;
  notifyExport();
  await loadList(view.generation);
}
